import { useMemo } from 'react';
import {
  getCellWidths,
  getColumnByType,
  getRowCells,
  getRowHeight,
  updateDataByType
} from '../utils/functions';
import { CustomHeaderCell } from '../../components/CustomHeaderCell';
import columnToLetter from '../../../../utils/columnToLetter';
import { deepCopy } from '../../../../utils/deepCopy';
import commonStore from '../../../../store/common';
import { MULTIPLE_TABLE_NAMES } from '../consts/tableSections';
import { useSyncExternalStore } from 'react';
import { reaction } from 'mobx';
import reportStore from '../../store';
import { useSearchParams } from 'react-router-dom';
import {
  FundDataRow,
  FundEntry,
  ReportGridColumn,
  ReportGridRow,
  UseMultipleTableProps,
  UseMultipleTableReturn
} from './types';
import { isFcaTableDisabledForFund } from '../utils/visibleSections';

interface MultipleTableFundLike extends FundEntry {
  fund_data: Record<string, FundDataRow[]>;
  previous_fund?: MultipleTableFundLike;
}

export default function useMultpleTable({
  data,
  onChangeFund,
  schema,
  isStaticTemplate,
  requiredFields,
  isMultiFunds,
  funds,
  onAddColumn,
  onDeleteColumn,
  tableName,
  columnLables,
  order = '',
  withoutActions = false
}: UseMultipleTableProps): UseMultipleTableReturn {
  const [searchParams] = useSearchParams();
  const type = searchParams.get('type');
  const currentDataKey = type === 'AIF001' ? 'AIFOO1' : searchParams.get('fund_id')!;
  const isTableDisabledForFund = fundOrId => {
    const fund =
      typeof fundOrId === 'object' && fundOrId !== null
        ? fundOrId
        : funds[String(fundOrId)] || funds[fundOrId];
    return isFcaTableDisabledForFund(fund, tableName);
  };
  const onChange = value => {
    const changes = value[0];
    const fund = isMultiFunds
      ? deepCopy(funds[changes.newCell.fundId].fund_data[tableName])
      : deepCopy(data);

    fund[changes.newCell.dataIndex][changes.newCell.dataKey] = updateDataByType({
      cell: changes.newCell
    });

    onChangeFund(tableName, fund, changes.newCell?.fundId);
  };

  const isComparisonReport = useSyncExternalStore(
    subscribe => {
      const dispose = reaction(() => reportStore.isComparisonReport, subscribe, {
        fireImmediately: false
      });
      return dispose;
    },
    () => reportStore.isComparisonReport
  );

  const contentWidth = useSyncExternalStore(
    subscribe => reaction(() => commonStore.contentWidth, subscribe, { fireImmediately: false }),
    () => commonStore.contentWidth
  );

  const columns = useMemo<ReportGridColumn[]>(() => {
    const _fundSize = Object.keys(funds).length;
    const _columns = isMultiFunds
      ? !isComparisonReport && _fundSize <= 3
        ? Array.from(Array(_fundSize + 1).keys()).map(index => ({
            columnId: index + 2,
            width: contentWidth / (_fundSize + 1)
          }))
        : isComparisonReport
          ? [
              {
                columnId: 2,
                width: 200
              },
              ...Object.values(funds)
                .map(item => [item, item?.previous_fund])
                .flat()
                .map((_, index) => ({
                  columnId: index + 3,
                  width: 300
                }))
            ]
          : [
              {
                columnId: 2,
                width: 200
              },
              ...Object.keys(funds).map((_, index) => ({
                columnId: index + 3,
                width: 300
              }))
            ]
      : isComparisonReport
        ? Array.from(Array(3).keys()).map(index => ({
            columnId: index + 2,
            width: contentWidth / 3
          }))
        : Array.from(Array(2).keys()).map(index => ({
            columnId: index + 2,
            width: contentWidth / 2
          }));

    return [
      {
        columnId: 0,
        width: 30
      },
      {
        columnId: 1,
        width: 30
      },
      ..._columns
    ];
  }, [isMultiFunds, funds, data, contentWidth, isComparisonReport]);

  const rows = useMemo(() => {
    if (!data) return [] as ReportGridRow[];

    const { labelWidth, valueWidth } = getCellWidths({
      isMultiFunds,
      isComparisonReport,
      fundSize: Object.keys(funds).length,
      contentWidth
    });

    let cells: ReportGridRow[] = [];
    let count = 0;
    const filteredData = data.filter(item => !item.is_deleted);
    const isVisibleDelete = filteredData.length > 1;

    if (isMultiFunds) {
      let columnIndex = 0;
      data.forEach((item, ind) => {
        if (!item.is_deleted) {
          let index2 = columnIndex;
          Object.keys(columnLables).map((key, index) => {
            if (index === 0) {
              cells.push({
                rowId: count,
                height: 40,
                cells: [
                  {
                    type: 'header',
                    text: `${order}`
                  },
                  {
                    type: 'header',
                    text: ''
                  },
                  {
                    type: 'customHeader',
                    text: '',
                    render: () => (
                      <CustomHeaderCell
                        title={`${MULTIPLE_TABLE_NAMES[tableName]} ${data.length > 1 ? index2 + 1 : ''}`}
                        onAddColumn={() => onAddColumn?.(tableName, ind)}
                        onDeleteColumn={() => onDeleteColumn?.(tableName, ind)}
                        hasDeleteBtn={isVisibleDelete && !withoutActions}
                        hasAddBtn={!withoutActions}
                      />
                    )
                  },
                  ...(isComparisonReport
                    ? (Object.values(funds) as MultipleTableFundLike[])
                        .map(item => [item, item?.previous_fund])
                        .flat()
                        .map((_, j) => ({
                          type: 'header',
                          text: item?.fund_name || '',
                          colspan: j % 2 === 0 ? 2 : undefined
                        }))
                    : Object.values(funds).map(item => ({
                        type: 'header',
                        text: item?.fund_name || '',
                        style: {
                          'justify-content': 'flex-start',
                          'white-space': 'inherit'
                        }
                      })))
                ]
              });
              count++;
            }

            if (isComparisonReport && index === 0) {
              cells.push({
                rowId: count,
                height: 40,
                cells: [
                  {
                    type: 'header',
                    text: ''
                  },
                  {
                    type: 'header',
                    text: ''
                  },
                  {
                    type: 'header',
                    text: ''
                  },
                  ...Object.values(funds)
                    .map(item => [item.previous_fund, item])
                    .flat()
                    .map(item => ({
                      type: 'header',
                      text: `${item!.period}, ${item!.year}`
                    }))
                ]
              });
              count++;
            }

            // Each value column stacks its own value above its own error, so
            // the row must fit the tallest column — pairing the longest value
            // with a different column's longest error over-sizes it.
            const rows = isComparisonReport
              ? (Object.values(funds) as MultipleTableFundLike[])
                  .map(fund => [fund.previous_fund, fund])
                  .flat()
                  .map(fund => fund!.fund_data[tableName][ind])
              : (Object.values(funds) as MultipleTableFundLike[]).map(
                  fund => fund.fund_data[tableName][ind]
                );

            const rowHeight = getRowHeight({
              label: columnLables[key]?.name,
              labelWidth,
              cells: getRowCells(rows, key, schema?.types?.[key]?.options, false),
              valueWidth
            });

            cells.push({
              rowId: count,
              height: rowHeight,
              cells: [
                {
                  type: 'header',
                  text: ''
                },
                {
                  type: 'header',
                  text: columnLables[key].withoutLetter ? '' : columnToLetter(index + 1)
                },
                {
                  type: 'customHeader',
                  text: '',
                  render: () => (
                    <CustomHeaderCell title={columnLables[key].name} hasAddBtn={false} />
                  )
                },
                ...(isComparisonReport
                  ? (Object.values(funds) as MultipleTableFundLike[])
                      .map(item => [item.previous_fund, item])
                      .flat()
                      .map((value: any, j) => {
                        const filterByEmptyObj = value.fund_data[tableName].filter(v => !v.id);
                        return getColumnByType({
                          type: schema?.types?.[key]?.type,
                          options: schema?.types?.[key]?.options,
                          key,
                          defaultValue: schema?.defaultValues?.[key],
                          index: ind,
                          value: value.fund_data[tableName][ind],
                          required:
                            (filterByEmptyObj.length === value.fund_data[tableName].length &&
                              columnIndex === 0) ||
                            value.fund_data[tableName][ind]?.id
                              ? funds[value.fund_id]?.required_tables?.[
                                  tableName!
                                ]?.field_names?.includes(key)
                              : false,
                          tableName,
                          isStaticTemplate,
                          fundId: value.fund_id,
                          isSectionDisabled: isTableDisabledForFund(value.fund_id),
                          fundName: value.fund_name,
                          hasDrillDownFeature:
                            !!value.fund_data[tableName][ind]?.report_id &&
                            value.fund_data[tableName][ind]?.[key] !== null &&
                            schema.types?.[key]?.has_drill_down_feature,
                          reportId: value.fund_data[tableName][ind]?.report_id,
                          tableId: value.fund_data[tableName][ind]?.id,
                          title: columnLables[key]?.name,
                          isMultiFunds,
                          fieldOrder:
                            typeof order === 'number' ? order + index : `${order}${index}`,
                          difference:
                            j % 2 === 1
                              ? funds[value.fund_id]?.difference?.[tableName]?.[ind]?.[key] || {}
                              : null,
                          nonEditable: isComparisonReport
                        });
                      })
                  : Object.values(funds).map(value => {
                      const filterByEmptyObj = value.fund_data[tableName].filter(v => !v.id);
                      return getColumnByType({
                        type: schema?.types?.[key]?.type,
                        options: schema?.types?.[key]?.options,
                        key,
                        defaultValue: schema?.defaultValues?.[key],
                        index: ind,
                        value: value.fund_data[tableName][ind],
                        required:
                          (filterByEmptyObj.length === value.fund_data[tableName].length &&
                            columnIndex === 0) ||
                          value?.fund_data?.[tableName]?.[ind]?.id
                            ? funds[value.fund_id!]?.required_tables?.[
                                tableName!
                              ]?.field_names?.includes(key)
                            : false,
                        tableName,
                        isStaticTemplate,
                        fundId: value.fund_id,
                        isSectionDisabled: isTableDisabledForFund(value.fund_id),
                        fundName: value.fund_name,
                        hasDrillDownFeature:
                          !!value.fund_data[tableName][ind]?.report_id &&
                          value.fund_data[tableName][ind]?.[key] !== null &&
                          schema.types?.[key]?.has_drill_down_feature,
                        reportId: value.fund_data[tableName][ind]?.report_id,
                        tableId: value.fund_data[tableName][ind]?.id,
                        title: columnLables[key]?.name,
                        isMultiFunds,
                        fieldOrder: typeof order === 'number' ? order + index : `${order}${index}`,
                        nonEditable: columnLables?.[key]?.disabled || isComparisonReport
                      });
                    }))
              ]
            });

            count++;
          });
          columnIndex++;
        }
      });
    } else {
      let columnIndex = 0;
      data.forEach((item, ind) => {
        if (!item.is_deleted) {
          let index2 = columnIndex;
          Object.keys(columnLables).forEach((key, index) => {
            if (index === 0) {
              cells.push({
                rowId: count,
                height: 40,
                cells: [
                  {
                    type: 'header',
                    text: `${order}`
                  },
                  {
                    type: 'header',
                    text: ''
                  },
                  {
                    type: 'customHeader',
                    text: '',
                    render: () => (
                      <CustomHeaderCell
                        title={`${MULTIPLE_TABLE_NAMES[tableName]} ${data.length > 1 ? index2 + 1 : ''}`}
                        onAddColumn={() => onAddColumn?.(tableName, ind)}
                        onDeleteColumn={() => onDeleteColumn?.(tableName, ind)}
                        hasDeleteBtn={isVisibleDelete && !withoutActions}
                        hasAddBtn={!withoutActions}
                      />
                    )
                  },

                  ...(isComparisonReport
                    ? [
                        {
                          type: 'header',
                          text: `${funds[currentDataKey].previous_fund?.period}, ${funds[currentDataKey].previous_fund?.year}`
                        },
                        {
                          type: 'header',
                          text: `${funds[currentDataKey].period}, ${funds[currentDataKey].year}`
                        }
                      ]
                    : [{ type: 'header', text: '' }])
                ]
              });
              count++;
            }

            // Each value column stacks its own value above its own error, so
            // the row must fit the tallest column — pairing the longest value
            // with a different column's longest error over-sizes it.
            const rows = isComparisonReport
              ? [funds[currentDataKey]!.previous_fund!.fund_data[tableName][ind], data?.[ind]]
              : [data?.[ind]];

            const rowHeight = getRowHeight({
              label: columnLables[key]?.name,
              labelWidth,
              cells: getRowCells(rows, key, schema?.types?.[key]?.options, false),
              valueWidth
            });

            const filterByEmptyObj = data.filter(v => !v.id);

            cells.push({
              rowId: count,
              height: rowHeight,
              cells: [
                {
                  type: 'header',
                  text: ''
                },
                {
                  type: 'header',
                  text: columnLables[key].withoutLetter ? '' : columnToLetter(index + 1)
                },
                {
                  type: 'customHeader',
                  text: '',
                  render: () => (
                    <CustomHeaderCell
                      title={columnLables[key].name}
                      hasDrillDown={
                        data[ind]?.report_id &&
                        (schema.types as any)[key].has_drill_down_feature &&
                        data[ind]?.[key] !== null
                      }
                      hasAddBtn={false}
                      reportId={data[ind]?.report_id}
                      fundId={data[ind].fund_id}
                      columnName={key}
                      tableName={tableName}
                      tableId={data[ind]?.id}
                    />
                  ),
                  style: {
                    'justify-content': 'flex-start',
                    'white-space': 'inherit'
                  }
                },
                ...(isComparisonReport
                  ? [
                      getColumnByType({
                        type: schema?.types?.[key]?.type,
                        options: schema?.types?.[key]?.options,
                        key,
                        defaultValue: schema?.defaultValues?.[key],
                        index: ind,
                        value: funds[currentDataKey]!.previous_fund!.fund_data[tableName][ind],
                        required:
                          (filterByEmptyObj.length === data.length && columnIndex === 0) || item?.id
                            ? requiredFields.includes(key)
                            : false,
                        tableName,
                        isStaticTemplate,
                        isMultiFunds,
                        fundId: funds[currentDataKey].previous_fund!.fund_id,
                        isSectionDisabled: isTableDisabledForFund(
                          funds[currentDataKey].previous_fund
                        ),
                        fundName: funds[currentDataKey].previous_fund!.fund_name,
                        fieldOrder: typeof order === 'number' ? order + index : `${order}${index}`,
                        nonEditable: isComparisonReport
                      })
                    ]
                  : []),
                getColumnByType({
                  type: schema?.types?.[key]?.type,
                  options: schema?.types?.[key]?.options,
                  key,
                  defaultValue: schema?.defaultValues?.[key],
                  index: ind,
                  value: item,
                  required:
                    (filterByEmptyObj.length === data.length && columnIndex === 0) || item?.id
                      ? requiredFields.includes(key)
                      : false,
                  tableName,
                  isStaticTemplate,
                  //height: getTextHeightForParagraph(String(item[key]), '150px'),
                  isMultiFunds,
                  fundId: item?.fund_id,
                  isSectionDisabled: isTableDisabledForFund(item?.fund_id),
                  fundName: item?.fund_name,
                  fieldOrder: typeof order === 'number' ? order + index : `${order}${index}`,
                  difference: isComparisonReport
                    ? funds[currentDataKey]?.difference?.[tableName]?.[ind]?.[key] || {}
                    : null,
                  nonEditable: columnLables?.[key]?.disabled || isComparisonReport
                })
              ]
            });

            count++;
          });
          columnIndex++;
        }
      });
    }

    return [...cells];
  }, [data, isMultiFunds, isStaticTemplate, funds, schema, isComparisonReport, currentDataKey]);

  return { rows, columns, onChange };
}
